// Endpoints organizados por dominio. Cada funcao recebe params tipados e retorna Promise<typed>.
// Tipos sao "loose" (Record<string, any>) porque vem do backend BsonDocument.

import { api } from './client';

// ============ AUTH ============
export const authApi = {
  login: (body: { email: string; password: string; totpCode?: string }) =>
    api.post<AuthTokens | { detail: string; pendingUserId?: string }>('/auth/login', body).then(r => r),
  refresh: (refreshToken: string) =>
    api.post<AuthTokens>('/auth/refresh', { refreshToken }).then(r => r.data),
  logout: (refreshToken: string) =>
    api.post('/auth/logout', { refreshToken }).then(r => r.data),
  logoutAll: () => api.post('/auth/logout-all').then(r => r.data),
  me: () => api.get<MeResponse>('/auth/me').then(r => r.data),
  updateMe: (body: { displayName?: string; email?: string }) =>
    api.patch<MeResponse & { emailChanged?: boolean }>('/auth/me', body).then(r => r.data),
  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    api.post('/auth/change-password', body).then(r => r.data),
  // TOTP
  setupTotp: () =>
    api.post<{ secretBase32: string; otpAuthUri: string; qrCodePngBase64: string; backupCodes: string[] }>('/auth/totp/setup').then(r => r.data),
  confirmTotp: (code: string) => api.post('/auth/totp/confirm', { code }).then(r => r.data),
  disableTotp: (code: string) => api.post('/auth/totp/disable', { code }).then(r => r.data),
  // Password reset
  requestReset: (email: string) => api.post('/auth/password-reset/request', { email, portalUrl: window.location.origin }).then(r => r.data),
  confirmReset: (token: string, newPassword: string) => api.post('/auth/password-reset/confirm', { token, newPassword }).then(r => r.data),
  // Convite (admin) — cria usuario em estado pendente e dispara e-mail com link unico
  inviteUser: (body: { email: string; displayName: string; role: 'admin' | 'manager'; portalUrl?: string }) =>
    api.post<{ userId: string; email: string; displayName: string; role: string; inviteExpiresAtUtc: string; inviteEmailSent: boolean }>('/auth/invite', body).then(r => r.data),
  getInvite: (token: string) =>
    api.get<{ email: string; displayName: string; role: string; expiresAtUtc: string; createdByEmail: string }>(`/auth/invite/${encodeURIComponent(token)}`).then(r => r.data),
  startInviteTotp: (token: string) =>
    api.post<{ secretBase32: string; otpAuthUri: string; qrCodePngBase64: string; backupCodes: string[] }>(`/auth/invite/${encodeURIComponent(token)}/totp/setup`).then(r => r.data),
  acceptInvite: (body: { token: string; newPassword: string; totpCode: string }) =>
    api.post<AuthTokens>('/auth/invite/accept', body).then(r => r.data),
  // ===== IP change (autorizacao de novo IP) =====
  getIpChange: (token: string) =>
    api.get<{ maskedEmail: string; pendingIp: string; expiresAtUtc: string; trigger: string }>(
      `/auth/ip-change/${encodeURIComponent(token)}`
    ).then(r => r.data),
  confirmIpChange: (token: string, password: string) =>
    api.post<{ ok: boolean }>(`/auth/ip-change/${encodeURIComponent(token)}/confirm`, { password }).then(r => r.data),
  // ===== Discord OAuth (vinculo) =====
  discordLinkStart: () =>
    api.get<{ authorizeUrl: string }>('/auth/discord/link/start').then(r => r.data),
  discordUnlink: () =>
    api.post<{ ok: boolean }>('/auth/discord/unlink').then(r => r.data)
};

export interface AuthTokens {
  accessToken: string;
  accessExpiresAtUtc: string;
  refreshToken: string;
  refreshExpiresAtUtc: string;
  userId: string;
  email: string;
  displayName: string;
  role: 'admin' | 'manager';
}

export interface MeResponse {
  userId: string;
  email: string;
  displayName: string;
  role: 'admin' | 'manager';
  isActive: boolean;
  isTotpEnabled: boolean;
  createdAtUtc: string;
  lastLoginAtUtc?: string;
  trustedIp?: string | null;
  trustedIpSetAtUtc?: string | null;
  discord?: {
    id: string;
    username: string;
    avatar?: string;
    email?: string;
    linkedAtUtc?: string;
  } | null;
}

// ============ DASHBOARDS ============
export const dashboardsApi = {
  overview: () => api.get<any>('/dashboards/overview').then(r => r.data),
  topCheaters: (limit = 50) => api.get<any[]>('/dashboards/top-cheaters', { params: { limit } }).then(r => r.data),
  topArtifacts: (limit = 50, hashType?: 'md5' | 'sha256') =>
    api.get<any[]>('/dashboards/top-artifacts', { params: { limit, hashType } }).then(r => r.data),
  topModules: (limit = 50) => api.get<any[]>('/dashboards/top-modules', { params: { limit } }).then(r => r.data),
  topIps: (limit = 50) => api.get<any[]>('/dashboards/top-ips', { params: { limit } }).then(r => r.data),
  timeline: (detectionType?: string) => api.get<any[]>('/dashboards/timeline', { params: { detectionType } }).then(r => r.data),
  typeDistribution: () => api.get<any[]>('/dashboards/type-distribution').then(r => r.data)
};

// ============ EMULATORS (CodeShield Emulator Guard) ============
export const emulatorsApi = {
  // rollup por família (top emuladores)
  top: (limit = 50) => api.get<any[]>('/emulators/top', { params: { limit } }).then(r => r.data),
  // cabeçalho de dashboard: totais 7d + distribuição por reason
  summary: () => api.get<any>('/emulators/summary').then(r => r.data),
  // detecções paginadas/filtradas
  list: (params: {
    family?: string; reason?: string; hwid?: string; minRisk?: number;
    onlyActionable?: boolean; from?: string; to?: string; historic?: boolean;
    page?: number; pageSize?: number;
  } = {}) =>
    api.get<{ page: number; pageSize: number; totalApprox: number; historic: boolean; items: any[] }>(
      '/emulators', { params }).then(r => r.data),
  // drill por máquina
  byHwid: (hwid: string, opts: { historic?: boolean; skip?: number; limit?: number } = {}) =>
    api.get<{ hwid: string; total: number; returned: number; hasMore: boolean; items: any[] }>(
      `/emulators/hwid/${encodeURIComponent(hwid)}`, { params: opts }).then(r => r.data)
};

// ============ DRILL-DOWN ============
export interface DrillBucket { total: number; skip: number; limit: number; returned: number; hasMore: boolean; items: any[] }
export interface DrillResponse {
  entity: { type: string; value: string };
  generatedAtUtc: string;
  historic: boolean;
  pagination: { skip: number; perCollection: number };
  mark?: any;
  relatedCases: any[];
  detections: { cheat: DrillBucket; dll: DrillBucket; files: DrillBucket };
  sessions: { auth: DrillBucket; disconnects: DrillBucket };
  identity: { nicks: DrillBucket };
  totals: {
    detectionCheat: number; detectionDll: number; detectionFiles: number;
    sessionAuth: number; sessionDisconnect: number; identityNick: number;
    grandTotalDetections: number;
  };
}

export const drillApi = {
  hwid: (hwid: string, opts: { historic?: boolean; perCollection?: number; skip?: number } = {}) =>
    api.get<DrillResponse>(`/drill/hwid/${encodeURIComponent(hwid)}`, { params: opts }).then(r => r.data),
  user: (userId: number, opts: { historic?: boolean; perCollection?: number; skip?: number } = {}) =>
    api.get<DrillResponse>(`/drill/user/${userId}`, { params: opts }).then(r => r.data),
  nick: (nick: string, opts: { historic?: boolean; perCollection?: number } = {}) =>
    api.get<any>(`/drill/nick/${encodeURIComponent(nick)}`, { params: opts }).then(r => r.data),
  file: (md5: string, opts: { historic?: boolean; perCollection?: number; skip?: number } = {}) =>
    api.get<DrillResponse>(`/drill/file/${encodeURIComponent(md5)}`, { params: opts }).then(r => r.data),
  ip: (ip: string, opts: { historic?: boolean; perCollection?: number; skip?: number } = {}) =>
    api.get<DrillResponse>(`/drill/ip/${encodeURIComponent(ip)}`, { params: opts }).then(r => r.data)
};

// ============ MARKS ============
export const marksApi = {
  upsert: (body: any) => api.post<any>('/marks', body).then(r => r.data),
  search: (params: any) => api.get<{ page: number; pageSize: number; totalApprox: number; items: any[] }>('/marks', { params }).then(r => r.data),
  get: (id: string) => api.get<any>(`/marks/${id}`).then(r => r.data),
  delete: (id: string) => api.delete(`/marks/${id}`).then(r => r.data),
  lookup: (entityType: string, entityValue: string) =>
    api.get<{ found: boolean; mark?: any }>('/marks/lookup', { params: { entityType, entityValue } }).then(r => r.data),
  batchLookup: (entityType: string, values: string[]) =>
    api.post<Record<string, any>>('/marks/batch-lookup', { entityType, values }).then(r => r.data)
};

// ============ CASES ============
export const casesApi = {
  create: (body: any) => api.post<any>('/cases', body).then(r => r.data),
  get: (id: string) => api.get<any>(`/cases/${id}`).then(r => r.data),
  update: (id: string, body: any) => api.patch<any>(`/cases/${id}`, body).then(r => r.data),
  transition: (id: string, body: { status: string; resolution?: string }) =>
    api.post<any>(`/cases/${id}/transition`, body).then(r => r.data),
  search: (params: any) => api.get<{ page: number; pageSize: number; totalApprox: number; items: any[] }>('/cases', { params }).then(r => r.data),
  addComment: (caseId: string, body: string) => api.post<any>(`/cases/${caseId}/comments`, { body }).then(r => r.data),
  listComments: (caseId: string) => api.get<any[]>(`/cases/${caseId}/comments`).then(r => r.data)
};

// ============ USERS (admin) ============
export const usersApi = {
  list: (params: any) => api.get<{ page: number; pageSize: number; total: number; items: any[] }>('/users', { params }).then(r => r.data),
  get: (id: string) => api.get<any>(`/users/${id}`).then(r => r.data),
  update: (id: string, body: any) => api.patch<any>(`/users/${id}`, body).then(r => r.data),
  resetTotp: (id: string) => api.post(`/users/${id}/reset-totp`).then(r => r.data),
  revokeSessions: (id: string) => api.post(`/users/${id}/revoke-sessions`).then(r => r.data),
  sessions: (id: string) => api.get<any[]>(`/users/${id}/sessions`).then(r => r.data),
  loginHistory: (id: string) => api.get<any[]>(`/users/${id}/login-history`).then(r => r.data),
  setPassword: (id: string, newPassword: string) => api.post(`/users/${id}/set-password`, { newPassword }).then(r => r.data),
  // ===== Convites (admin) =====
  // Regenera convite (token novo, TTL 7d), envia email e retorna URL pro admin
  // copiar/enviar manualmente caso o email nao chegue
  resendInvite: (id: string, portalUrl?: string) =>
    api.post<{ url: string; expiresAtUtc: string; emailSent: boolean }>(
      `/users/${id}/invite/resend`,
      { portalUrl: portalUrl ?? window.location.origin }
    ).then(r => r.data),
  cancelInvite: (id: string) => api.delete(`/users/${id}/invite`).then(r => r.data),
  // ===== Remover usuario =====
  remove: (id: string) => api.delete(`/users/${id}`).then(r => r.data)
};

// ============ NOTIFICATIONS ============
export interface NotificationHistoryParams {
  userId?: string;
  channel?: string;     // email | discord | telegram
  status?: string;      // sent | failed | queued
  eventType?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}
export const notificationsApi = {
  listSubscriptions: (userId?: string) =>
    api.get<any[]>('/notifications/subscriptions', { params: { userId } }).then(r => r.data),
  subscribe: (body: { userId?: string; eventType: string; channel: 'email' | 'discord' | 'telegram'; target: string }) =>
    api.post<any>('/notifications/subscriptions', body).then(r => r.data),
  setActive: (id: string, isActive: boolean) =>
    api.patch(`/notifications/subscriptions/${id}/active`, { isActive }).then(r => r.data),
  history: (params: NotificationHistoryParams) =>
    api.get<{ page: number; pageSize: number; totalApprox: number; items: any[] }>('/notifications/history', { params }).then(r => r.data),
  test: (body: any) => api.post<any>('/notifications/test', body).then(r => r.data),
  emit: (body: any) => api.post<any>('/notifications/emit', body).then(r => r.data)
};

// ============ AUDIT ============
export const auditApi = {
  list: (params: any) => api.get<{ page: number; pageSize: number; totalApprox: number; items: any[] }>('/audit', { params }).then(r => r.data),
  forEntity: (entityType: string, entityId: string) =>
    api.get<any[]>(`/audit/entity/${entityType}/${entityId}`).then(r => r.data)
};

// ============ REPORTS ============
export const reportsApi = {
  list: (params: { status?: string; reportType?: string; page?: number; pageSize?: number } = {}) =>
    api.get<{ page: number; pageSize: number; totalApprox: number; items: any[] }>('/reports', { params }).then(r => r.data),
  generate: (reportType: string, parameters: any) =>
    api.post<{ jobId: string; status: string }>('/reports/generate', { reportType, parameters }).then(r => r.data),
  get: (id: string) => api.get<any>(`/reports/${id}`).then(r => r.data),
  // Solicita um token de download temporário (5 min, single-use) e retorna a URL completa.
  // O <a href> ou window.location.href pode usar essa URL diretamente, sem Authorization header.
  requestDownloadUrl: async (id: string): Promise<{ url: string; expiresAtUtc: string }> => {
    const { data } = await api.post<{ url: string; expiresAtUtc: string }>(`/reports/${id}/download-token`);
    const baseUrl = api.defaults.baseURL ?? '';
    return { url: `${baseUrl}${data.url}`, expiresAtUtc: data.expiresAtUtc };
  }
};

// ============ CONFIG / SETTINGS ============
export const configApi = {
  listSettings: (category?: string) => api.get<any[]>('/config/settings', { params: { category } }).then(r => r.data),
  updateSetting: (key: string, value: any) => api.patch<any>(`/config/settings/${encodeURIComponent(key)}`, { value }).then(r => r.data),
  listFlags: () => api.get<any[]>('/config/feature-flags').then(r => r.data),
  updateFlag: (key: string, value: boolean) => api.patch<any>(`/config/feature-flags/${encodeURIComponent(key)}`, { value }).then(r => r.data),
  auditConfig: (params: any) => api.get<{ page: number; pageSize: number; items: any[] }>('/config/audit', { params }).then(r => r.data)
};

// ============ THEMES ============
export const themesApi = {
  list: (kind?: string) => api.get<any[]>('/themes', { params: { kind } }).then(r => r.data),
  get: (id: string) => api.get<any>(`/themes/${id}`).then(r => r.data),
  create: (body: any) => api.post<any>('/themes', body).then(r => r.data),
  update: (id: string, body: any) => api.patch<any>(`/themes/${id}`, body).then(r => r.data),
  delete: (id: string) => api.delete(`/themes/${id}`).then(r => r.data),
  myPreferences: () => api.get<any>('/me/preferences').then(r => r.data),
  setPreferences: (body: any) => api.patch<any>('/me/preferences', body).then(r => r.data)
};

// ============ CATALOG ============
export interface CatalogListParams {
  kind?: 'exe' | 'dll' | 'driver' | 'asset' | 'script' | 'other';
  status?: string;          // suspect | allowed | clean | known_cheater | false_positive | investigating | exempt
  search?: string;
  from?: string;
  to?: string;
  minRisk?: number;
  historic?: boolean;
  page?: number;
  pageSize?: number;
}
export interface BulkAllowlistFile {
  name: string;
  relPath?: string;
  ext?: string;
  size?: number;
  md5?: string;
  sha256?: string;
}
export interface BulkAllowlistResult {
  totalFiles: number;
  totalHashesProvided: { md5: number; sha256: number };
  matchedInDetections: { md5: number; sha256: number };
  marks: { wouldCreate: number; created: number; updated: number; totalAffected: number };
  dryRun: boolean;
  sampleMatches: { kind: string; value: string; name: string; relPath?: string }[];
}
export const catalogApi = {
  list: (params: CatalogListParams = {}) =>
    api.get<{ page: number; pageSize: number; totalApprox: number; historic: boolean; items: any[] }>('/catalog', { params }).then(r => r.data),
  mark: (sourceHash: string, body: { status: string; severity?: number; notes?: string; tags?: string[]; key?: 'source_hash' | 'module_path' | 'file_md5'; linkedCaseId?: string }) =>
    api.post<any>(`/catalog/${encodeURIComponent(sourceHash)}/mark`, body).then(r => r.data),
  bulkAllowlist: (body: { files: BulkAllowlistFile[]; localDirLabel?: string; extraTag?: string; dryRun?: boolean }) =>
    api.post<BulkAllowlistResult>('/catalog/bulk-allowlist', body, { timeout: 120_000 }).then(r => r.data)
};

// ============ CHANGELOG ============
export const changelogApi = {
  list: (limit = 50, category?: string) => api.get<any[]>('/changelog', { params: { limit, category } }).then(r => r.data),
  get: (version: string) => api.get<any>(`/changelog/${version}`).then(r => r.data),
  create: (body: any) => api.post<any>('/changelog', body).then(r => r.data),
  update: (version: string, body: any) => api.patch<any>(`/changelog/${version}`, body).then(r => r.data),
  delete: (version: string) => api.delete(`/changelog/${version}`).then(r => r.data)
};

// ============ LIVE PLAYERS ============
// Snapshot inicial + acoes. Updates em tempo real chegam via SignalR (ver lib/liveHub.ts).
export interface LiveDetectionBreakdown { cheat: number; dll: number; files: number; }
export interface LiveMark { status: string; severity: number; tags: string[]; }
export interface LivePlayer {
  hwid: string;
  clientId?: number;
  userId?: number;
  ipAddress?: string;
  nickname?: string;
  characterName?: string;
  loginId?: number;
  authenticatedAtUtc: string;
  sessionDurationSeconds: number;
  detectionsRecentTotal: number;
  detectionsRecent: LiveDetectionBreakdown;
  maxRiskLevel: number;
  mark?: LiveMark;
}
export interface LiveSnapshot {
  generatedAtUtc: string;
  source: 'mongo' | 'server' | 'hybrid' | 'server_unavailable';
  windowSeconds: number;
  players: LivePlayer[];
}
export type LiveSource = 'auto' | 'server' | 'mongo';
export interface LiveSourceStatus {
  serverEnabled: boolean;
  lastSource?: string;
  lastSnapshotAtUtc?: string;
}
export const liveApi = {
  snapshot: (source: LiveSource = 'auto') =>
    api.get<LiveSnapshot>('/live/players', { params: { source } }).then(r => r.data),
  sourceStatus: () =>
    api.get<LiveSourceStatus>('/live/players/source-status').then(r => r.data),
  drill: (hwid: string) =>
    api.get<any>(`/live/players/${encodeURIComponent(hwid)}/drill`).then(r => r.data),
  watch: (hwid: string, body?: { severity?: number; tags?: string[]; notes?: string }) =>
    api.post<any>(`/live/players/${encodeURIComponent(hwid)}/watch`, body ?? {}).then(r => r.data),
  ban: (hwid: string, body: { confirm: 'BANIR'; severity?: number; tags?: string[]; notes?: string }) =>
    api.post<any>(`/live/players/${encodeURIComponent(hwid)}/ban`, body).then(r => r.data),
  kick: (clientId: number, reason?: string) =>
    api.post<{ kicked: boolean; clientId: number; reason: string }>(
      `/live/players/${clientId}/kick`, { reason }
    ).then(r => r.data)
};

// ============ HARDWARE (inventario das maquinas) ============
export interface HardwareSearchParams {
  q?: string;
  osBuild?: number;
  gpuVendor?: string;
  tpm?: boolean;
  secureBoot?: boolean;
  minRamGB?: number;
  maxRamGB?: number;
  page?: number;
  pageSize?: number;
}
export const hardwareApi = {
  latest: (hwid: string) =>
    api.get<any>(`/hardware/${encodeURIComponent(hwid)}`).then(r => r.data),
  history: (hwid: string, page = 1, pageSize = 50) =>
    api.get<{ hwid: string; page: number; pageSize: number; totalApprox: number; items: any[] }>(
      `/hardware/${encodeURIComponent(hwid)}/history`, { params: { page, pageSize } }).then(r => r.data),
  search: (params: HardwareSearchParams) =>
    api.get<{ page: number; pageSize: number; totalApprox: number; items: any[] }>(
      '/hardware', { params }).then(r => r.data),
  compare: (a: string, b: string) =>
    api.get<{ a: any; b: any; matches: Record<string, any>; sharedComponentCount: number; bothFound: boolean }>(
      '/hardware/compare', { params: { a, b } }).then(r => r.data),
  overview: () =>
    api.get<any>('/hardware/overview').then(r => r.data),
};

// ============ HOT TIER reads (mirror collections raw) =====
// Pode adicionar conforme necessidade. Endpoints novos no backend pra listas filtradas?
// Vamos usar drill/dashboards. Para lista geral, sumarizamos via dashboards/* primeiro.
